'use client';

import {
    REGEXP_ONLY_CHARS,
    REGEXP_ONLY_DIGITS,
    REGEXP_ONLY_DIGITS_AND_CHARS,
} from 'input-otp';
import { useTranslations } from 'next-intl';
import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import CustomButton from '../custom-button';
import {
    InputOTPGroup,
    InputOTPSlot,
    InputOTP as ShadcnInputOTP,
} from '../ui/input-otp';

export interface InputOTPProps {
    range?: number;
    callbackReSendOTP?: VoidFunction;
    value?: string;
    onChange?: (value: string) => void;
    /**
     * Pattern cho input
     * @default 'only-digits'
     */
    pattern?: 'only-chars' | 'only-digits' | 'chars-and-digits';
    description?: ReactNode;
}

/**
 * Thời gian cooldown để cho phép nhấn gửi lại OTP
 */
const TIME_TO_RESEND = 60;

const InputOTP = memo((props: InputOTPProps) => {
    const {
        range = 6,
        callbackReSendOTP,
        pattern = 'only-digits',
        description,
        ...rest
    } = props;

    const transDef = useTranslations('System');

    let _regex: string;
    let _inputMode: string = 'text';

    switch (pattern) {
        case 'only-chars':
            _regex = REGEXP_ONLY_CHARS;
            break;

        case 'chars-and-digits':
            _regex = REGEXP_ONLY_DIGITS_AND_CHARS;
            break;
        case 'only-digits':
        default:
            _regex = REGEXP_ONLY_DIGITS;
            _inputMode = 'numeric';
            break;
    }

    return (
        <div className='my-3 max-w-full sm:my-2'>
            <div className='flex flex-col items-center gap-2 sm:flex-row max-lg:justify-center'>
                <ShadcnInputOTP
                    maxLength={range}
                    {...rest}
                    pattern={_regex}
                    inputMode={_inputMode as any}
                >
                    <InputOTPGroup>
                        {Array.from({ length: range }).map((_, index) => (
                            <InputOTPSlot
                                index={index}
                                key={index}
                                className='h-12 w-12 bg-white text-lg font-semibold'
                            />
                        ))}
                    </InputOTPGroup>
                </ShadcnInputOTP>

                {typeof callbackReSendOTP === 'function' && (
                    <ButtonCooldown callback={callbackReSendOTP} />
                )}
            </div>
            {description ?? (
                <p className='mt-1 text-xs text-gray-400'>
                    {transDef('System_PlsEnterTheCodeSentToYourMail')}
                </p>
            )}
        </div>
    );
});

const ButtonCooldown = memo(({ callback }: { callback: VoidFunction }) => {
    const transDef = useTranslations('System');

    const [coolDown, setCoolDown] = useState(TIME_TO_RESEND);

    const interval = useRef<NodeJS.Timeout>();
    useEffect(() => {
        if (coolDown === 0) return;
        interval.current = setInterval(() => {
            setCoolDown((prev) => {
                if (prev === 0) {
                    clearInterval(interval.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval.current);
    }, [coolDown]);

    const onResend = () => {
        callback();
        setCoolDown(TIME_TO_RESEND);
    };

    return (
        <CustomButton.Root
            size='sm'
            disabled={coolDown !== 0}
            className='min-w-28 disabled:opacity-70 h-8 text-[13px]'
            onClick={onResend}
        >
            {transDef('System_ReSend')} {coolDown > 0 && `(${coolDown}s)`}
        </CustomButton.Root>
    );
});

ButtonCooldown.displayName = 'ButtonCooldown';
InputOTP.displayName = 'InputOTP';
export { InputOTP };
